import {
  addFiscaisAquisicaoSheet,
  addPartesEnvolvidasSheet,
  addResponsaveisAquisicaoSheet,
  addTermosAditivosSheet,
  addTramitacoesSeiSheet,
  fetchGenericEntityData,
} from './auxiliar_componentes';
import { getTipoEnvolvimentoLabel } from '../../../modules/types/TipoEnvolvimentoEnum';
import { EnumOption } from '../../../modules/types/types';
import { valoresExibidos } from '../../../pages/daf/ProcessosAquisitivos/acts/planoTrabalho/auxiliarPlanoTrabalho';
import { getAllPlanosTrabalhoAct } from '../../../services/acts/actsPlanoTrabalhoService';
import { ExportSheet } from '../core';
import { baseLogColumns, DataPreparer } from '../factory';

export class ActExportPreparer implements DataPreparer {
  private modulo = 'act';

  /**
   * Busca todos os dados relacionados aos ACTs selecionados
   */
  private async fetchRelatedData(actIds: number[]): Promise<any> {
    if (actIds.length === 0) return {};

    try {
      const [allPlanos] = await Promise.all([this.fetchActPlanosDeTrabalho()]);
      const planos = this.filterByActIds(allPlanos, actIds);

      const [
        partesEnvolvidas,
        fiscaisAct,
        responsaveisAct,
        termosAditivos,
        tramitacoesSei,
      ] = await Promise.all([
        fetchGenericEntityData('parteenvolvida', this.modulo, actIds),
        fetchGenericEntityData('fiscalaquisicao', this.modulo, actIds),
        fetchGenericEntityData('responsavelaquisicao', this.modulo, actIds),
        fetchGenericEntityData('termoaditivo', this.modulo, actIds),
        fetchGenericEntityData('tramitacoes', this.modulo, actIds),
      ]);

      return {
        planos,
        partesEnvolvidas,
        fiscaisAct,
        responsaveisAct,
        termosAditivos,
        tramitacoesSei,
      };
    } catch (error) {
      console.error('Erro ao buscar dados relacionados do ACT:', error);
      return {};
    }
  }

  private async fetchActPlanosDeTrabalho(): Promise<any[]> {
    try {
      const result = await getAllPlanosTrabalhoAct();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Erro ao buscar fases do Act:', error);
      return [];
    }
  }
  private filterByActIds(data: any[], actsIds: number[]): any[] {
    if (!Array.isArray(data) || data.length === 0) return [];

    return data.filter((item) => {
      // Acessa o id do Act através de duas formas possíveis
      const actId = item.act?.id || item.actId;
      return actsIds.includes(actId);
    });
  }

  async prepareForExport(
    selectedActs: any[],
    additionalData?: {
      statusActOption?: EnumOption[];
      componenteAF?: EnumOption[];
    },
  ): Promise<ExportSheet[]> {
    const sheets: ExportSheet[] = [];
    const actIds = selectedActs.map((a) => a.id);

    const { statusActOption = [], componenteAF = [] } = additionalData || {};

    // 1. Sheet principal - ACTs
    sheets.push({
      name: 'Acordos Coop. Técnica',
      data: selectedActs.map((act) =>
        this.transformAct(act, statusActOption, componenteAF),
      ),
      columns: {
        ...baseLogColumns,
        numeroSeiAct: 'Número SEI',
        numeroAct: 'Nº ACT',
        statusAct: 'Status',
        componenteAf: 'Componente AF',
        fornecedor: 'Fornecedor',
        fornecedorPapel: 'Papel do Fornecedor',
        dataDou: 'Data D.O.U.',
        dataInicio: 'Data Início',
        dataVigencia: 'Data Vigência',
        objeto: 'Objeto',
      },
    });

    // 2. Buscar dados relacionados
    const relatedData = await this.fetchRelatedData(actIds);

    // 3. Adicionar sheets dos dados relacionados
    this.addPlanosSheet(sheets, relatedData.planos);
    addPartesEnvolvidasSheet(sheets, this.modulo, relatedData.partesEnvolvidas);
    addFiscaisAquisicaoSheet(sheets, this.modulo, relatedData.fiscaisAct);
    addResponsaveisAquisicaoSheet(
      sheets,
      this.modulo,
      relatedData.responsaveisAct,
    );
    addTermosAditivosSheet(sheets, this.modulo, relatedData.termosAditivos);
    addTramitacoesSeiSheet(sheets, this.modulo, relatedData.tramitacoesSei);

    return sheets;
  }

  /**
   * Transformação principal do ACT
   */
  private transformAct = (
    act: any,
    statusActOption: EnumOption[],
    componenteAF: EnumOption[],
  ) => {
    return {
      //logs
      id: act.id,
      usuarioRegistro: act.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: act.dataRegistro,
      dataUltimaAtualizacao: act.dataUltimaAtualizacao,
      usuarioAtualizacao: act.usuarioAtualizacao?.nomeCompleto || 'N/A',
      usuarioUltimaVisualizacao:
        act.usuarioUltimaVisualizacao?.nomeCompleto || 'N/A',
      nEdicoes: act.nEdicoes || 0,
      nVisualizacoes: act.nVisualizacoes || 0,
      nVisualizadores: act.nVisualizadores || 0,

      // módulo
      numeroSeiAct: act.numeroSeiAct || 'N/A',
      numeroAct: act.numeroAct || 'N/A',
      statusAct: statusActOption[act.statusAct]?.label || act.statusAct,
      componenteAf: componenteAF[act.componenteAf]?.label || act.componenteAf,
      fornecedor: act.fornecedor?.razaoSocial || 'N/A',
      fornecedorPapel: getTipoEnvolvimentoLabel(act.fornecedorPapel) || 'N/A',
      dataDou: act.dataDou,
      dataInicio: act.dataInicio,
      dataVigencia: act.dataVigencia,
      objeto: act.objeto || 'N/A',

      // observações
      observacoes: act.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Planos se houver dados
   */
  private addPlanosSheet(sheets: ExportSheet[], planos?: any[]) {
    if (!planos?.length) return;

    sheets.push({
      name: 'Planos de Trabalho',
      data: planos.map(this.transformPlano),
      columns: {
        ...baseLogColumns,
        ...valoresExibidos,
        produtoDAF: 'Produto Farmacêutico',
        actId: 'ID ACT',
        numeroAct: 'Nº ACT',
      },
    });
  }

  private transformPlano = (plano: any) => {
    return {
      //logs
      id: plano.id,
      usuarioRegistro: plano.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: plano.dataRegistro,
      dataUltimaAtualizacao: plano.dataUltimaAtualizacao,
      usuarioAtualizacao: plano.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: plano.nEdicoes || 0,
      nVisualizacoes: plano.nVisualizacoes || 0,
      nVisualizadores: plano.nVisualizadores || 0,

      //Dados da ACT
      actId: plano.act?.id || 'N/A',
      numeroAct: plano.act.numeroAct || 'N/A',
      produtoDAF: plano.produtoDAF.produtoFarmaceutico,

      //Dados do Plano da ACT
      embalagemFator: plano.embalagemFator ? plano.embalagemFator : '0',

      //Quantidades
      qtdAdministrativa: plano.qtdAdministrativa
        ? plano.qtdAdministrativa
        : '0',
      qtdEstrategico: plano.qtdEstrategico ? plano.qtdEstrategico : '0',
      qtdJudicial: plano.qtdJudicial ? plano.qtdJudicial : '0',
      qtdOutro: plano.qtdOutro ? plano.qtdOutro : '0',

      //Valores
      valorUnitHomologado: plano.valorUnitHomologado
        ? plano.valorUnitHomologado
        : '',
      valorUnitReequilibrio:
        plano.valorReequilibrioBin === 'S'
          ? plano.valorUnitReequilibrio
          : 'N/A',
      observacoes: plano.observacoes || '',
    };
  };
}
